// Journal des exports (spec § 2), lu par les administrateurs seulement (politique de la table `export_log`).
import { useQuery } from "@tanstack/react-query";
import { useLocaleState, useTranslate } from "ra-core";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { getSupabaseClient } from "../providers/supabase/supabase";

type ExportLine = {
  id: number;
  created_at: string;
  resource: string;
  format: string;
  row_count: number;
  filters: Record<string, unknown>;
  sales: { first_name: string; last_name: string } | null;
};

/** Filtres en cours au moment de l'export, tels que la liste les a transmis. */
const filterText = (filters: Record<string, unknown>) =>
  Object.entries(filters)
    .map(
      ([key, value]) =>
        `${key} = ${Array.isArray(value) ? value.join(", ") : String(value)}`,
    )
    .join(" · ");

export const ExportLogList = () => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const { data, error, isPending } = useQuery({
    queryKey: ["export_log"],
    // Relu à chaque ouverture : un export vient peut-être d'être fait.
    refetchOnMount: "always",
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("export_log")
        .select(
          "id, created_at, resource, format, row_count, filters, sales(first_name, last_name)",
        )
        .order("created_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return data as unknown as ExportLine[];
    },
  });

  return (
    <div className="mt-2 pb-4 flex flex-col gap-4">
      <div>
        <h2 className="text-xl font-semibold">
          {translate("resources.projects.export_log.title")}
        </h2>
        <p className="text-sm text-muted-foreground max-w-3xl">
          {translate("resources.projects.export_log.intro")}
        </p>
      </div>
      {error ? (
        <p className="text-sm text-destructive">{error.message}</p>
      ) : isPending ? null : !data?.length ? (
        <p className="text-sm text-muted-foreground">
          {translate("resources.projects.export_log.empty")}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              {(
                [
                  "created_at",
                  "author",
                  "resource",
                  "format",
                  "rows",
                  "filters",
                ] as const
              ).map((key) => (
                <TableHead
                  key={key}
                  className={key === "rows" ? "text-right" : undefined}
                >
                  {translate(`resources.projects.export_log.${key}`)}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((line) => (
              <TableRow key={line.id}>
                <TableCell className="whitespace-nowrap">
                  {new Date(line.created_at).toLocaleString(locale, {
                    dateStyle: "short",
                    timeStyle: "short",
                  })}
                </TableCell>
                <TableCell>
                  {line.sales
                    ? `${line.sales.first_name} ${line.sales.last_name}`
                    : ""}
                </TableCell>
                <TableCell>
                  {translate(
                    `resources.projects.export_log.resources.${line.resource}`,
                  )}
                </TableCell>
                <TableCell className="uppercase">{line.format}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {line.row_count.toLocaleString(locale)}
                </TableCell>
                <TableCell className="text-muted-foreground whitespace-normal">
                  {filterText(line.filters) ||
                    translate("resources.projects.export_log.no_filter")}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};
